<template>
    <flex-element ref="carousel" :element-type="elementType" class="f-relative f-flex">
        <transition-group
            tag="div"
            name="transition-slide"
            :enter-active-class="slideAnimationClasses.enter"
            :leave-active-class="slideAnimationClasses.leave"
            class="f-overflow-x-hidden f-w-1/1"
        >
            <!-- ---------- slides (editor + public): ---------- -->
            <div
                v-for="(rendering, index) in elementsNew"
                v-show="showSlide(index)"
                :key="rendering.id"
                class="fu-placement-fill"
            >
                <component
                    :is="rendering.elementType"
                    class="f-w-1/1"
                    v-bind="rendering"
                    :index="index"
                    :pointer="index + 1"
                    is-carousel-component
                    @element-updated="elementUpdated"
                />
            </div>
        </transition-group>
        <!-- ---------- carousel controls (public): ---------- -->
        <!-- arrows: -->
        <button
            type="button"
            class="fu-placement-left f-p-2.5 f-text-3 f-opacity-50 hover:f-opacity-100 f-text-gray-400 f-z-20 f-cursor-pointer f-flex f-items-center"
            aria-label="previous slide"
            @click="onArrowBackClick"
        >
            <a-icon icon="chevron-left" />
        </button>

        <button
            type="button"
            class="fu-placement-right f-p-2.5 f-text-3 f-opacity-50 hover:f-opacity-100 f-text-gray-400 f-z-20 f-cursor-pointer f-flex f-items-center"
            aria-label="next slide"
            @click="onArrowNextClick"
        >
            <a-icon icon="chevron-right" />
        </button>

        <!-- bullets: -->
        <!-- TODO: same as above -- the indices are hard-coded; we should be able to automatically detect number of slides: -->
        <div v-if="controlsAreVisible" class="f-flex f-flex-row f-justify-center f-z-20 fu-placement-bottom f-mb-2 mob:f-hidden bullets">
            <a-icon
                v-for="(n, index) in elementsNew"
                :key="index"
                :class="{ 'f-opacity-50': (index !== currentSlideIndex) }"
                class="f-mx-0.25 f-cursor-pointer"
                icon="circle"
                set="fas"
                aria-label="select slide"
                @click="onBulletClick(index)"
            />
        </div>
    </flex-element>
</template>

<script>
import baseOrganism from '@/mixins/base-organism/base-organism-dumb';
import Timer from '@/utils/timer';

const DEFAULT_CAROUSEL_SPEED = '4'; // in seconds (String)

export default {
    mixins: [baseOrganism],

    data() {
        return {
            slideDirection: 'next',
            timer: null,
            currentSlideIndex: 0,
        };
    },

    computed: {
        carouselSpeed() {
            let output;
            let speed = DEFAULT_CAROUSEL_SPEED;

            if (this.hasElementData('carousel-speed')) {
                speed = this.getElementData('carousel-speed');
            }

            if (speed.toLowerCase() === 'none') {
                output = 999999999;

            // convert seconds to milliseconds:
            } else {
                output = Number(speed) * 1000;
            }

            return output;
        },

        slideAnimationClasses() {
            let cssObject;

            switch (this.getElementData('carousel-transition-effect')) {
                case 'slide':
                    if (this.slideDirection === 'prev') {
                        cssObject = {
                            enter: 'slideInLeft animated fast',
                            leave: 'slideOutRight animated fast',
                        };
                    } else {
                        cssObject = {
                            enter: 'slideInRight animated fast',
                            leave: 'slideOutLeft animated fast',
                        };
                    }
                    break;

                case 'slide reverse':
                    if (this.slideDirection === 'prev') {
                        cssObject = {
                            enter: 'slideInRight animated fast',
                            leave: 'slideOutLeft animated fast',
                        };
                    } else {
                        cssObject = {
                            enter: 'slideInLeft animated fast',
                            leave: 'slideOutRight animated fast',
                        };
                    }
                    break;

                case 'fade':
                    cssObject = {
                        enter: 'fadeIn animated',
                        leave: 'fadeOut animated',
                    };
                    break;
                default:
                    break;
            }

            return cssObject;
        },

        controlsAreVisible() {
            return this.elementsNew.length > 1 && !this.hasElementData('hide-carousel-controls');
        },
    },

    created() {
        this.atomicType = 'organism';
        this.templateType = 'flex-carousel';

        // if transition effect is not set, set default:
        if (!this.getElementData('carousel-transition-effect')) {
            this.setElementData('carousel-transition-effect', 'slide');
        }
    },

    mounted() {
        // automatically change slides on a regular interval:
        this.timer = new Timer(this.onArrowNextClick, this.carouselSpeed);
        this.timer.start();
    },

    methods: {
        showSlide(index) {
            return index === this.currentSlideIndex;
        },

        toPrevSlide() {
            this.slideDirection = 'prev';

            // if current slide is at the end of the list, go to first slide:
            if (this.currentSlideIndex === 0) {
                this.currentSlideIndex = this.elementsNew.length - 1;
            } else {
                this.currentSlideIndex--;
            }
        },

        toNextSlide() {
            this.slideDirection = 'next';

            // if current slide is at the end of the list, go to first slide:
            if (this.currentSlideIndex === this.elementsNew.length - 1) {
                this.currentSlideIndex = 0;
            } else {
                this.currentSlideIndex++;
            }
        },

        onArrowBackClick() {
            this.toPrevSlide();

            this.timer.reset(this.carouselSpeed);
        },

        onArrowNextClick() {
            this.toNextSlide();

            this.timer.reset(this.carouselSpeed);
        },

        onBulletClick(clickedIndex) {
            // early return:
            if (clickedIndex === this.currentSlideIndex) {
                return;
            }

            if (clickedIndex > this.currentSlideIndex) {
                this.slideDirection = 'next';
            } else if (clickedIndex < this.currentSlideIndex) {
                this.slideDirection = 'prev';
            }
            this.currentSlideIndex = clickedIndex;

            this.timer.reset(this.carouselSpeed);
        },
    },
};
</script>
